import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { defualtUserApiLimit } from 'src/app/app/app.constants';
import { CompanyConfigurationTotal, OutliersTotal } from 'src/app/common/stats-utils';
import { splitIntoBulkInviteBatches } from 'src/app/services/invite/bulk-invite-batch.util';
import { BulkInvitePayload, BulkInviteResponse, Invitation, User } from 'src/models';
import { ApiLimitParams, ApiService } from '../api.service';
import { PagedApiResult, PagedResult, PagingService } from '../paging/paging.service';
export type UserApiDetail = 'id' | 'name' | 'alias' | 'tags' | 'info' | 'settings' | 'managers' | 'silent';

export interface UserMapped<T = any> {
  user: User;
  data: T;
}

export interface UserApiParams extends ApiLimitParams {
  user?: string | string[];
  tag?: string | string[];
  'archived-tag'?: string | string[];
  'task-project-names'?: boolean;
  self?: boolean | 'include' | 'exclude';
  deleted?: 1 | 0;
  'delete-permanently'?: 1 | 0;
  detail?: UserApiDetail;
  sort?: string;
  'silent-details'?: 1 | 0;
  'filter[id]'?: string | string[];
  'filter[hiredAt]'?: string | string[];
  'filter[os]'?: string | string[];
  'filter[email]'?: string;
  'filter[keyphrase]'?: string;
  'filter[show-on-reports]'?: 1 | 0;
  'filter[invite-pending]'?: 1 | 0;
  'filter[last-track]'?: string;
  'include-archived-users'?: 1 | 0;
  'from-payroll'?: 1 | 0;
  [key: string]: string | number | boolean | string[];
}

export interface InviteExist {
  id: string;
  name: string;
  invitePending: boolean;
}

export interface InviteExtraOptions {
  name?: string;
  employeeId?: string;
  noSendEmail?: boolean;
  tagIds?: string[];
  password?: string;
}

export type SilentZombieGrouping = 'hostName' | 'machineId' | 'ip';
export interface GetSilentZombiesOptions {
  days?: number; // default 14
  'group-by'?: SilentZombieGrouping[]; // (default: hostName)
  sort?: string;
  filter?: string;
  limit?: number;
  page?: number;
}

export interface SilentZombieUser {
  id: string;
  name: string;
  lastTracked: string;
  lastAccessAttempt: string;
  userInfo: {
    NameCanonicalEx: string;
    NameDnsDomain: string;
    NameFullyQualifiedDN: string;
    NameSamCompatible: string;
    NameUniqueId: string;
  };
  os: string;
  client: string;
}

export interface SilentZombie {
  ip: string | string[];
  hostName: string | string[];
  machineId: string | string[];
  users: SilentZombieUser[];
}

export interface OutliersCounts {
  tooMuchUnprodTime: number;
  tooManyWorkLifeIssues: number;
  tooManyWorkHours: number;
}

export interface GetOutliersCountsOptions {
  from: string;
  to: string;
  user: string;
  company: string;
  interval?: number;
  period?: string;
  'user-tz'?: number;
  // API cache time in seconds
  'api-cache'?: number;
  'api-cache-ignore'?: string;
  'include-users-limit'?: number;
}
export interface GetCompanyConfigurationOptions {
  from: string;
  to: string;
  user: string;
  // API cache time in seconds
  'api-cache'?: number;
}

export interface RoleCountsResponse {
  all: number;
  admin: number;
  owner: number;
  manager: number;
  user: number;
  client: number;
  'pending-invites': number;
}

export type GetRoleCountsOptions = Partial<UserApiParams>;

const defaultPagingLimit = defualtUserApiLimit;
type ApprovalAction = 'approve' | 'block' | 'delete';
type HttpMethod = 'put' | 'delete';

/**
 * Virtual scroll needs totalCount on page 0; subsequent pages must skip the
 * expensive backend $facet count (no-total=1), even if callers pass no-total=0.
 */
export function usersRequestNoTotal(
  page: number,
  explicit?: number | boolean,
): 0 | 1 {
  if (page > 0) {
    return 1;
  }
  return explicit === 1 || explicit === true ? 1 : 0;
}

interface ApproveOrBlockBody {
  approved?: boolean;
  blocked?: boolean;
}

interface BulkApprovalOrBlockBody {
  updates: { userId: string, approved?: boolean, blocked?: boolean }[];
}

interface BulkDeleteBody {
  userIds: string[];
}

type RequestBody = ApproveOrBlockBody | BulkApprovalOrBlockBody | BulkDeleteBody | undefined;

interface ApiResponse {
  status?: number;
  data?: unknown;
}



@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private api: ApiService, private paging: PagingService) {
  }

  get(id: string, deleted: boolean = false, params: UserApiParams = {}) {
    return this.api.request<{ data: User }>('get', `users/${id}`, {
      ...(params || {}),
      deleted: deleted ? 1 : 0,
    }).pipe(map(x => x.data));
  }

  users(params: UserApiParams = {}) {
    if (params?.user?.length === 0) {
      return this.paging.empty<User>();
    }
    const limit = params?.limit || defaultPagingLimit;
    const pendingApprovalEndpoints = ['pending-approval', 'approved', 'blocked'];
    const endpoint = (pendingApprovalEndpoints.includes(params.tab as string)) ? 'pending-approvals' : 'users';
    const isBlockedView = params.tab === 'blocked';
    const isApprovedView = params.tab === 'approved';

    return this.paging.getPagedResult((page) =>
      this.api.request<PagedApiResult<User>>('get', endpoint, {
        page, ...params, limit,
        // Must win over ...params so scroll pages never recompute totalCount.
        'no-total': usersRequestNoTotal(page, params['no-total'] as 0 | 1 | undefined),
        ...(params.user && {user: '' + params.user}),
        ...(params.tag && {tag: '' + params.tag}),
        ...(isBlockedView && {blocked: 1}),
        ...(isApprovedView && {approved: 1}),
      }), limit);
  }

  usersMapped<T, V = UserMapped<T>>(
    params: UserApiParams,
    mapFn: (userIds: string[]) => Promise<T[] | Map<string, T>>,
    zipFn: (user: User, dt: T) => boolean,
    finalMapFn?: (item: UserMapped<T>) => V,
  ): PagedResult<V> {
    if (params?.user?.length === 0) {
      return this.paging.empty<V>();
    }

    const limit = params?.limit || defaultPagingLimit;

    return (params['filter[id]']?.length === 0 || params.tag?.length === 0) ? this.paging.empty<V>() :
      this.paging.getPagedResult((page) =>
          this.api.request<PagedApiResult<User>>('get', `users`, {
            page, ...params, limit,
            // Must win over ...params so scroll pages never recompute totalCount.
            'no-total': usersRequestNoTotal(page, params['no-total'] as 0 | 1 | undefined),
            ...(params.user && {user: '' + params.user}),
            ...(params.tag && {tag: '' + params.tag}),
          }).pipe(switchMap(userResult => {
            if (!userResult?.data) {
              return null;
            }
            const userIds = userResult.data.map(y => y.id);
            return mapFn(userIds).then(res => ({
              ...userResult,
              data: (() => {
                const data = userResult.data.map(user => ({
                  user,
                  data: res instanceof Map ? res.get(user.id) : res.find(r => zipFn(user, r)),
                }));

                return (finalMapFn ? data.map(finalMapFn) : data) as V[];
              })(),
            }));
          })),
        limit);
  }

  editProperties(id: string, properties: Partial<User>, params?: { [param: string]: string | string[] }) {
    return this.api.request('put', `users/${id}`, properties, params ? { params } : undefined);
  }

  /**
   * this is a temporary function in order to show soft/hide the soft delete feature
   * see https://globalworkforce.jira.com/browse/STAFF-128103
   */
  deleteNoSoftDelete(id: string, reason: string) {
    return this.api.request('delete', `users/${id}`, {}, {params: {reason}});
  }

  delete(id: string, archivedTab: boolean, reason: string) {
    if (archivedTab) {
      return this.api.request('delete', `users/${id}`, {}, {params: {'delete-permanently': '1', deleted: '1'}});
    }
    return this.api.request('delete', `users/${id}`, {}, {params: {'delete-permanently': '1', reason}});
  }

  archive(id: string, reason: string) {
    return this.api.request('delete', `users/${id}`, {}, {params: {'delete-permanently': '0', reason}});
  }

  restore(id: string) {
    return this.api.request('delete', `users/${id}`, {}, {params: {deleted: '1'}});
  }

  resendInvite(id: string) {
    return lastValueFrom(this.api.request('get', `invitations/resend/${id}`, null, null, null, '1.1'));
  }

  userExists(email: string) {
    return lastValueFrom(this.api.request<{
      data: InviteExist;
    }>('get', 'invitations/exists', {email}, null, null, '1.1'));
  }

  usersExistBulk(emails: string[]) {
    return lastValueFrom(this.api.request<{
      data: Record<string, InviteExist>;
    }>('post', 'invitations/exists/bulk', { emails }, null, true, '1.1'));
  }

  invite(email: string, role: 'user' | 'guest' | 'admin' | 'manager', options: InviteExtraOptions = null) {
    return lastValueFrom(this.api.request<{ data: Invitation }>('post', 'invitations', {
      ...options,
      email,
      role,
    }, null, null, '1.1'));
  }

  async bulkInvite(bulkInvites: BulkInvitePayload) {
    const batches = splitIntoBulkInviteBatches(bulkInvites.users);
    const data: BulkInviteResponse[] = [];

    for (const users of batches) {
      try {
        const result = await lastValueFrom(this.api.request<{
          data: BulkInviteResponse[];
        }>('post', 'invitations/bulk', { ...bulkInvites, users }, null, true, '1.1'));
        data.push(...(result.data ?? []));
      } catch (error) {
        // Earlier batches may have already invited users on the server. Return those
        // results so callers can still run billing/tracking for successful invites.
        if (data.length > 0) {
          return { data };
        }
        throw error;
      }
    }

    return { data };
  }

  getTotpQRCode() {
    return lastValueFrom(this.api.request<{ data: { qrUrl: string } }>('get', 'login/2fa/new')).then(x => x.data);
  }

  activateTotp(totpCode: string) {
    return lastValueFrom(this.api.request<{ data: { status: 'totpActive' } }>('post', 'login/2fa/activate', {totpCode}))
      .then(x => x.data);
  }

  resendConfirmationEmail() {
    return lastValueFrom(this.api.request('post', `profile/confirmation`, null, null, null, '1.1'));
  }

  getSilentZombies(params: GetSilentZombiesOptions = {}) {
    const baseUrl = 'silent-zombies';
    const limit = params?.limit || defaultPagingLimit;

    return this.paging.getPagedResult((page) =>
      this.api.request<PagedApiResult<SilentZombie>>('get', baseUrl,
        {page, ...params, 'group-by': params['group-by'] + '', limit}, null, true, '1.1'), limit);
  }

  getCompanyConfigurationCounts(company: string, params?: GetCompanyConfigurationOptions) {
    return lastValueFrom(this.api.request<{
      data: CompanyConfigurationTotal;
    }>('get', `users/totals`, {company, ...params, 'api-cache': 24 * 60 * 60}).pipe(map(x => x.data)));
  }

  getOutliersCounts(params: GetOutliersCountsOptions) {
    return lastValueFrom(this.api.request<{
      data: OutliersTotal;
      cachedAt?: string;
    }>('get', `stats/outliers`, {...params}, null, null, '1.1'));
  }

  async getRoleCounts(params?: GetRoleCountsOptions): Promise<RoleCountsResponse> {
    // Forward all filter params from asUserApiParams(); limit / no-total are
    // overridden below purely for counting, so no need to strip them first.
    const baseParams = {
      ...(params || {}),
      // Override specific params for count optimization
      limit: 1, // We only need totalCount, not actual data
      'no-total': 0, // We need the total count
      deleted: params?.deleted ?? 0,
    };

    // Get counts for each role using existing GET /users with filter[role]
    // Each request has error handling to prevent one failure from breaking all counts
    const [allResult, adminResult, ownerResult, managerResult, userResult, clientResult, pendingInvitesResult] = await Promise.allSettled([
      // All users (no role filter)
      lastValueFrom(this.api.request<PagedApiResult<User>>('get', 'users', baseParams)),
      // Admin users
      lastValueFrom(this.api.request<PagedApiResult<User>>('get', 'users', { ...baseParams, 'filter[role]': 'admin' })),
      // Owner users
      lastValueFrom(this.api.request<PagedApiResult<User>>('get', 'users', { ...baseParams, 'filter[role]': 'owner' })),
      // Manager users
      lastValueFrom(this.api.request<PagedApiResult<User>>('get', 'users', { ...baseParams, 'filter[role]': 'manager' })),
      // Regular users
      lastValueFrom(this.api.request<PagedApiResult<User>>('get', 'users', { ...baseParams, 'filter[role]': 'user' })),
      // Client/guest users
      lastValueFrom(this.api.request<PagedApiResult<User>>('get', 'users', { ...baseParams, 'filter[role]': 'guest' })),
      // Users who have not activated their account
      lastValueFrom(this.api.request<PagedApiResult<User>>('get', 'users', { ...baseParams, 'filter[invite-pending]': 1 })),
    ]);

    // Helper to safely extract count from settled promise result
    const getCount = (result: PromiseSettledResult<PagedApiResult<User>>) =>
      result.status === 'fulfilled' ? result.value.paging?.totalCount ?? 0 : 0;

    return {
      all: getCount(allResult),
      admin: getCount(adminResult),
      owner: getCount(ownerResult),
      manager: getCount(managerResult),
      user: getCount(userResult),
      client: getCount(clientResult),
      'pending-invites': getCount(pendingInvitesResult),
    };
  }

  async handleManualApprovalForSilentUsers(
    action: ApprovalAction,
    userIds: string[],
  ): Promise<{ success: boolean, status: number, data?: unknown, error?: unknown, action: ApprovalAction }> {
    const ids = Array.isArray(userIds) ? userIds : [userIds];
    const isBulk = ids.length > 1;
    const httpMethod: HttpMethod = action === 'delete' ? 'delete' : 'put';

    const { url, body } = this.buildRequest(action, ids, isBulk);

    try {
      const response = await lastValueFrom(
        this.api.request<ApiResponse>(
          httpMethod,
          url,
          httpMethod === 'delete' && !isBulk ? undefined : body,
        ),
      );
      const { status = 200 } = response;
      return { success: true, status, data: response?.data ?? null, action };
    } catch (error) {
      const status = error?.status || error?.response?.status || 500;
      return { success: false, status, error, action };
    }
  }

  private buildRequest(
    action: ApprovalAction,
    ids,
    isBulk: boolean,
  ): { url: string, body: RequestBody } {

    switch (action) {
      case 'approve':
      case 'block':
        if (isBulk) {
          return {
            url: 'pending-approvals/bulk',
            body: {
              updates: ids.map((id) => ({
                userId: id._ids || id,
                ...(action === 'approve' ? {approved: true, blocked: false} : {blocked: true, approved: false}),
              })),
            },
          };
        }
        return {
          url: `pending-approvals/${ids[0]?._id || ids[0]}`,
          body: {
            ...(action === 'approve' ? {approved: true, blocked: false} : {blocked: true, approved: false}),
          },
        };

      case 'delete':
        if (isBulk) {
          return {
            url: 'pending-approvals/bulk',
            body: {userIds: ids.map(id => id._id || id)},
          };
        }
        return {
          url: `pending-approvals/${ids[0]?._id || ids[0]}`,
          body: undefined,
        };
    }

  }
}
